import { memo } from 'react';
import type { ConstraintsGridProps } from './ConstraintsGrid.types';
import { getWidgetStores } from '@/entities/widget';
import { MotionBox } from '@/shared/ui/motion';
import { useTranslation } from 'react-i18next';

interface ConstraintTagProps {
  label: string;
}

/**
 * ConstraintTag - A styled tag chip for constraint titles
 * Memoized to prevent unnecessary re-renders
 */
const ConstraintTag = memo(function ConstraintTag({ label }: ConstraintTagProps) {
  return (
    <span className="inline-flex items-center justify-center gap-1 rounded-lg bg-destructive-soft px-3 py-2">
      <span className="text-center text-body-lg font-medium whitespace-nowrap text-destructive-soft-foreground">
        {label}
      </span>
    </span>
  );
});

interface ConstraintItemProps {
  constraint: {
    title: string;
    content: string;
  };
}

/**
 * ConstraintItem - Individual constraint card
 * Memoized to prevent re-renders when sibling constraints change
 */
const ConstraintItem = memo(function ConstraintItem({ constraint }: ConstraintItemProps) {
  return (
    <li className="flex flex-col items-start justify-center gap-4 pb-6">
      {/* Tag - using title from schema */}
      <ConstraintTag label={constraint.title} />

      {/* Content - using content from schema */}
      <p className="w-full text-body-lg text-foreground">{constraint.content}</p>
    </li>
  );
});

/**
 * ConstraintsGrid displays campaign constraints in a responsive grid layout
 *
 * Features:
 * - Responsive layout (horizontal in fullscreen, vertical in panel mode)
 * - Accessible with proper ARIA labels and semantic structure
 * - Visual constraint tags for categorization
 * - Memoized to prevent unnecessary re-renders
 */
export const ConstraintsGrid = memo(function ConstraintsGrid({
  constraints,
}: ConstraintsGridProps) {
  const { t } = useTranslation('contentTest');

  const { sidePanelLayoutMode } = getWidgetStores().uiStore.useSidePanelLayoutMode();
  const isFullscreen = sidePanelLayoutMode === 'fullscreen';

  if (constraints.length === 0) {
    return null;
  }

  return (
    <section
      aria-labelledby="constraints-title"
      className="flex w-full items-start justify-center border-b border-border-light px-15"
    >
      <div
        className={`flex flex-1 items-start gap-18 border-t border-border-light py-20 ${
          isFullscreen ? 'flex-row' : 'flex-col'
        }`}
      >
        {/* Left Title. Its width is a share of the 1440px design width rather
            than a fixed size, so the title column scales with the viewport. */}
        <h2
          id="constraints-title"
          className="shrink-0 text-display-sm text-foreground"
          style={{ minWidth: `${(448 / 1440) * 100}vw` }}
        >
          {t('ideationWidget.constraintsGrid.constraints')}
        </h2>

        {/* Right Cards */}
        <MotionBox
          layout="preserve-aspect"
          component="ul"
          role="list"
          aria-label={t('ideationWidget.constraintsGrid.campaignConstraints')}
          // Two fixed columns by design; add media queries here if the grid ever
          // needs to reflow on narrow viewports. The column gap is a percentage
          // of the grid, which has no canonical step.
          className="m-0 grid flex-1 list-none grid-cols-2 gap-x-[5.83%] gap-y-6 p-0"
        >
          {constraints.map((constraint, index) => (
            <ConstraintItem key={`constraint-${index}`} constraint={constraint} />
          ))}
        </MotionBox>
      </div>
    </section>
  );
});
